import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import TotalKlickLogo from "@/components/TotalKlickLogo";
import ThemeToggle from "@/components/ThemeToggle";
import { useTheme } from "@/contexts/ThemeContext";
import {
  LogOut, LayoutDashboard, Boxes, ShoppingBag, Network as NetworkIcon, ScrollText, Store,
  Settings, FileText, Upload, Users, UserCheck, Palette, Mail, Eye, Image, FileSpreadsheet, Layers, Sparkles, Wand2, Inbox, Library, TrendingUp, Repeat2,
} from "lucide-react";

const menusByRole = {
  superadmin: [
    { to: "/superadmin", icon: LayoutDashboard, label: "Panoramica", testid: "nav-overview" },
    { to: "/superadmin/networks", icon: NetworkIcon, label: "Networks", testid: "nav-networks" },
    { to: "/superadmin/users", icon: UserCheck, label: "Utenti & Impersona", testid: "nav-users" },
    { to: "/superadmin/customers", icon: Users, label: "Clienti centralizzati", testid: "nav-customers" },
    { to: "/superadmin/csv-bulk", icon: Upload, label: "CSV / XLSX Bulk", testid: "nav-sa-csv" },
    { to: "/superadmin/schema", icon: FileSpreadsheet, label: "Schema Builder", testid: "nav-sa-schema" },
    { to: "/casa-madre/catalog-transfer", icon: Repeat2, label: "Esporta/Importa Catalogo", testid: "nav-sa-catalog-transfer" },
    { to: "/superadmin/audit", icon: ScrollText, label: "Audit log", testid: "nav-audit" },
  ],
  casa_madre: [
    { to: "/casa-madre", icon: LayoutDashboard, label: "Dashboard", testid: "nav-cm-dashboard" },
    { to: "/casa-madre/products", icon: Boxes, label: "Catalogo", testid: "nav-cm-products" },
    { to: "/casa-madre/librerie", icon: Library, label: "Librerie / Archivi", testid: "nav-cm-librerie" },
    { to: "/casa-madre/configuratore", icon: Sparkles, label: "Configuratore prodotti", testid: "nav-cm-configurator" },
    { to: "/casa-madre/ai-import", icon: Wand2, label: "Importa da PDF (AI)", testid: "nav-cm-ai-import" },
    { to: "/casa-madre/config-templates", icon: Layers, label: "Template configuratore", testid: "nav-cm-config-templates" },
    { to: "/casa-madre/po-inbox", icon: ShoppingBag, label: "PO Rivenditori", testid: "nav-cm-po-inbox" },
    { to: "/casa-madre/prenotazioni", icon: Inbox, label: "Prenotazioni B2C", testid: "nav-cm-prenotazioni" },
    { to: "/casa-madre/richieste", icon: Mail, label: "Richieste configurazione", testid: "nav-cm-richieste" },
    { to: "/casa-madre/orders", icon: ShoppingBag, label: "Ordini & Tracking", testid: "nav-cm-orders" },
    { to: "/casa-madre/crm", icon: Mail, label: "CRM conto terzi", testid: "nav-cm-crm" },
    { to: "/casa-madre/storefront-catalog", icon: Store, label: "Vetrina catalogo", testid: "nav-cm-vetrina-catalog" },
    { to: "/casa-madre/media", icon: Image, label: "Media Library", testid: "nav-cm-media" },
    { to: "/casa-madre/external-brands", icon: UserCheck, label: "Brand esterni", testid: "nav-cm-external" },
    { to: "/casa-madre/cms", icon: Palette, label: "CMS vetrina", testid: "nav-cm-cms" },
    { to: "/casa-madre/catalog-transfer", icon: Repeat2, label: "Esporta/Importa Catalogo", testid: "nav-cm-catalog-transfer" },
    { to: "/casa-madre/settings", icon: Settings, label: "Impostazioni", testid: "nav-cm-settings" },
  ],
  rivenditore: [
    { to: "/rivenditore", icon: LayoutDashboard, label: "Dashboard", testid: "nav-riv-dashboard" },
    { to: "/rivenditore/catalog", icon: Store, label: "Catalogo vetrina", testid: "nav-riv-catalog" },
    { to: "/rivenditore/acquista", icon: ShoppingBag, label: "Acquista da Casa Madre", testid: "nav-riv-acquista" },
    { to: "/rivenditore/eredita", icon: TrendingUp, label: "Eredita catalogo CM", testid: "nav-riv-inherit" },
    { to: "/rivenditore/external-products", icon: Boxes, label: "Brand esterni", testid: "nav-riv-external" },
    { to: "/rivenditore/prenotazioni", icon: Inbox, label: "Prenotazioni B2C", testid: "nav-riv-prenotazioni" },
    { to: "/rivenditore/orders", icon: ShoppingBag, label: "Ordini & Tracking", testid: "nav-riv-orders" },
    { to: "/rivenditore/cms", icon: Palette, label: "CMS vetrina", testid: "nav-riv-cms" },
    { to: "/rivenditore/settings", icon: Settings, label: "Impostazioni", testid: "nav-riv-settings" },
  ],
};

export default function AdminLayout({ children }) {
  const { user, logout, impersonating } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  if (!user) {
    navigate("/login");
    return null;
  }

  const menu = menusByRole[user.role] || [];

  // Find tenant slug for "vedi sito" button
  const tenantSlug = user?.tenant_slug || null;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-layout">
      <ImpersonationBanner />
      <div className="flex">
        <aside className={`w-64 flex flex-col fixed ${impersonating ? "top-10" : "top-0"} bottom-0 left-0 transition-colors duration-300 ${
          isDark ? "bg-[#0a0a0c] border-r border-white/10" : "bg-white border-r border-border"
        }`}>
          <div className={`px-5 py-6 border-b ${isDark ? "border-white/10" : "border-border"}`}>
            <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
              <TotalKlickLogo size={36} />
              <div className="leading-tight">
                <div className={`font-heading font-bold text-[15px] ${isDark ? "text-white" : ""}`}>TotalKlick</div>
                <div className={`overline ${isDark ? "text-zinc-500" : "text-muted-foreground"}`}>Hub</div>
              </div>
            </Link>
          </div>

          <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
            {menu.map((item) => {
              const Icon = item.icon;
              const isRoleRoot = ["/superadmin", "/casa-madre", "/rivenditore", "/account"].includes(item.to);
              const active = location.pathname === item.to || (!isRoleRoot && location.pathname.startsWith(item.to + "/"));
              return (
                <Link key={item.to} to={item.to} data-testid={item.testid}
                  className={`flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                    active ? "bg-brand text-white" : isDark ? "text-zinc-400 hover:bg-white/5 hover:text-zinc-100" : "text-foreground hover:bg-accent"
                  }`}>
                  <Icon className="w-4 h-4" /> {item.label}
                </Link>
              );
            })}
          </nav>

          <div className={`p-3 border-t space-y-2 ${isDark ? "border-white/10" : "border-border"}`}>
            <div className="flex items-center gap-1.5">
              <LanguageSwitcher light={isDark} />
              <ThemeToggle surface={isDark ? "dark" : "light"} />
            </div>
            <div className={`px-3 py-2 rounded-sm ${isDark ? "bg-white/5" : "bg-accent"}`}>
              <div className={`text-[11px] uppercase tracking-wider ${isDark ? "text-zinc-500" : "text-muted-foreground"}`}>{user.role}</div>
              <div className={`text-sm font-medium truncate ${isDark ? "text-zinc-100" : ""}`} data-testid="user-name">{user.name || user.email}</div>
            </div>
            <button data-testid="logout-btn" onClick={handleLogout}
              className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-sm border text-sm transition-colors ${
                isDark ? "border-white/10 text-zinc-300 hover:bg-white/5" : "border-border hover:bg-accent"
              }`}>
              <LogOut className="w-4 h-4" /> Esci
            </button>
          </div>
        </aside>

        <main className="flex-1 ml-64">{children}</main>
      </div>
    </div>
  );
}
